import React, { useState, useCallback, useContext, useEffect } from 'react';
import moment from 'moment';

import {
  StyledCard,
  StyledHeader,
  StyledSidebar,
  StyledContent,
} from 'modules/assignment-cards/styled';

import { VehicleInfo, JourneysInfo } from 'modules/assignment-cards/components';

import tel from 'utils/phone';

import { Icon, Flexer } from '@ontruck/orc';

import * as http from 'modules/assignment-map/http';

import AssignmentMapContext from 'modules/assignment-map/map/context';

import { StyledDriverCard, StyledAction, StyledVehicleWarning, StyledPositionText } from './styled';
import { ServiceTagFleetService, ServiceTagFTC } from 'components/ServiceTag';

const t = {
  vehicle_not_found: 'Vehicle unknown',
  position: 'Position',
};

const DriverCard = () => {
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState(null);
  const [journeys, setJourneys] = useState(null);
  const { driverSelected, vehicleSelected, positionTimestamp, itinerary, closeDriverBox } =
    useContext(AssignmentMapContext);

  const { stops = [] } = itinerary || {};

  const firstStop = stops[0] || {};
  const { time_from } = firstStop;

  const handleCloseClick = useCallback(() => {
    closeDriverBox(driverSelected);
  }, [driverSelected, closeDriverBox]);

  const handleJourneyClick = useCallback(() => {
    //noop
  }, []);

  useEffect(() => {
    const getDriver = async (driverId) => {
      if (!driverId) {
        return;
      }
      try {
        setLoading(true);
        const fetchedDriver = await http.getDriver(driverId);
        setDriver(fetchedDriver);
      } catch (err) {
        console.error(err);
        closeDriverBox();
      } finally {
        setLoading(false);
      }
    };

    getDriver(driverSelected);
    return () => {
      setDriver(null);
    };
  }, [driverSelected, closeDriverBox]);

  useEffect(() => {
    const getJourneys = async (driverId) => {
      if (!driverId || !time_from) {
        return;
      }
      const journeysByDriver = await http.getJourneysByDriver(
        driverId,
        moment(time_from).format('YYYY-MM-DD')
      );
      setJourneys(journeysByDriver[driverId]);
    };

    getJourneys(driverSelected);
    return () => {
      setJourneys(null);
    };
  }, [driverSelected, time_from]);

  if (!driverSelected) {
    return null;
  }

  const { contact_name, contact_phone, carrier = {}, vehicles = [] } = driver || {};

  const vehicle = (vehicles || []).find(({ id }) => id === parseInt(vehicleSelected, 10));

  const vehicleModel = driver ? { vehicle, carrier } : null;

  const fromWhat = moment(positionTimestamp * 1000);

  return (
    <StyledDriverCard>
      <StyledCard loading={loading} data-testid="driver_card">
        <StyledHeader>
          <Flexer.Parent alignItems="center" fullWidth>
            <Flexer.Child flex="1" mLeft="XS" data-testid="driver_card_contact_name">
              {contact_name} {carrier?.is_ftc && <ServiceTagFTC size="S" />}
              {driver?.is_fleet_service && <ServiceTagFleetService size="S" />}
            </Flexer.Child>
            <Flexer.Child>
              <StyledAction
                as="a"
                href={tel(contact_phone)}
                data-testid="driver_card_contact_phone"
              >
                <Icon glyph="phone" size="1.4rem" color="grey800" />
              </StyledAction>
            </Flexer.Child>
            <Flexer.Child>
              <StyledAction onClick={handleCloseClick} data-testid="driver_card_close">
                <Icon glyph="close" size="1.4rem" color="grey800" />
              </StyledAction>
            </Flexer.Child>
          </Flexer.Parent>
        </StyledHeader>
        <StyledSidebar>
          {!!vehicle && <VehicleInfo model={vehicleModel} />}
          {!loading && !vehicle && (
            <StyledVehicleWarning>{t.vehicle_not_found}</StyledVehicleWarning>
          )}
        </StyledSidebar>
        <StyledContent>
          {fromWhat.isValid() && (
            <StyledPositionText>
              {t.position} {fromWhat.fromNow()}
            </StyledPositionText>
          )}
          {!!journeys && (
            <>
              <JourneysInfo
                journeys={journeys}
                fetching={false}
                isRoute={false}
                onClick={handleJourneyClick}
              />
            </>
          )}
        </StyledContent>
      </StyledCard>
    </StyledDriverCard>
  );
};

export default DriverCard;
